import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import { GetDocumentListPayload } from '@/domains/documents/store/types/DocumentsSlice';
import { RootState } from '@/store/types/RootState';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsDynamicOverrideMode } from '@/features/eds/enums/EdsDynamicOverrideMode';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { edsToEntityFormat } from '@/features/eds/services/utils/edsToCodeFormat';
import { SnackType } from '@/features/snacks/enums/SnackType';
import {
  getDocumentInstanceList,
  getDocumentInstanceListDataError,
  getDocumentInstanceListSuccess,
  incrementDocumentInstancesPage,
  startLoadingDocumentInstanceListData,
} from '@/domains/documents/store/slices/documentInstancesSlice/documentInstancesSlice';
import { SortDirection } from '@/enums/SortDirection';
import { DocumentService } from '@/services/DocumentService/DocumentService';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';
import SnacksService from '@/services/SnackService/SnackService';
import { limitDocumentCreatedByAccounts } from './utils/limitDocumentCreatedByAccounts';

const DOCUMENT_INCLUDES = [
  `${EdsEntityCodes.DocumentInstances}.${EdsEntityCodes.DocumentVersions}.${EdsEntityCodes.CreatedByPerson}.${EdsEntityCodes.Accounts}`,
  `${EdsEntityCodes.CreatedByPerson}.${EdsEntityCodes.Accounts}`,
  `${EdsEntityCodes.DocumentType}`,
].join(',');

export function* getDocumentInstancesSaga({ type, payload }: PayloadAction<GetDocumentListPayload>) {
  yield put(startLoadingDocumentInstanceListData());
  try {
    const pagination = yield select((state: RootState) => state.documents.data.pagination);
    const requestUrl = `/filter/${EdsEntityCodes.Document}?${QueryParamsService.constructSearchParamsFromRecord({
      page: pagination.page,
      perPage: pagination.perPage,
      sortBy: edsToEntityFormat('createdAt'),
      sortDirection: SortDirection.DESC,
      includes: DOCUMENT_INCLUDES,
    })}`;

    const { data } = yield call(HttpService.post, requestUrl, payload?.filters || [], {
      baseURL: import.meta.env.VITE_API_URL_EDS,
    });

    const normalizedResponse: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      dynamicOverrideMode: EdsDynamicOverrideMode.Merge,
      section: EdsDefinitionSections.SidePanel,
    });

    const transformedDataWithLatestVersion: NormalizedV2Response[] = yield call(
      Promise.all.bind(Promise),
      normalizedResponse.map(async (normalizedItem, index) => {
        return DocumentService.mergeLastInstanceVersionIntoDocumentFields(
          normalizedItem,
          data.listings[index].documentInstances,
          EdsDefinitionSections.SidePanel,
        );
      }),
    );

    const transformedDataWithSingleAccount = limitDocumentCreatedByAccounts(transformedDataWithLatestVersion);

    yield put(
      getDocumentInstanceListSuccess({
        shouldRewrite: type !== incrementDocumentInstancesPage.type,
        data: {
          listings: transformedDataWithSingleAccount,
          pagination: {
            ...data.pagination,
            isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
          },
        },
      }),
    );
  } catch (error) {
    console.error(error);
    SnacksService.add({
      type: SnackType.Error,
      title: 'Documents fetching error',
      content: 'Unable to fetch documents. Please try again.',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });
    yield put(getDocumentInstanceListDataError());
  }
}

export function* documentInstancesWatcher() {
  yield takeLatest([getDocumentInstanceList.type, incrementDocumentInstancesPage.type], getDocumentInstancesSaga);
}
